import React, {useEffect, useState} from "react";
import {Button, Col, Form, Row} from "react-bootstrap";
import DatePicker from "react-datepicker";
import {Banner, BannerStore} from "Stores/bannerStore";
import {School, SchoolStore} from "Stores/superStore/schools";
import {LabelValueOption, SelectWrapper} from "Components/Common/SelectWrapper";
import {reaction} from "mobx";
import BannerItem from "Components/Common/BannerItem";
import {SCHOOL_DATA_SOURCE_OPTIONS, SCHOOL_TYPE_OPTIONS} from "../../../../utils/constans";

interface BannerFormProps {
    banner?: Banner
    onCancel: () => void
    onSave: (banner: Banner, schools: string[], schoolTypes: string[], dataSources: string[], ruuid?: string) => void
    schoolStore: SchoolStore
    bannerStore: BannerStore
}

function BannerForm({onCancel, onSave, banner, schoolStore, bannerStore}: BannerFormProps) {
    const [schoolTypes, setSchoolTypes] = useState<LabelValueOption[]>(
        !banner ? [] : banner.school_types?.map(schoolType => ({value: schoolType.school_type, label: schoolType.school_type}))
    );
    const [text, setText] = useState(banner?.banner_text ?? "");
    const [description, setDescription] = useState(banner?.description ?? "");
    const [color, ] = useState(banner?.color ?? "#155724");
    const [backgroundColor, ] = useState(banner?.background_color ?? "#d4edda");
    const [linksColor, ] = useState(banner?.links_color ??"#007bff");
    const [isEnabled, setIsEnabled] = useState(banner?.enabled ?? true);
    const [isAdmins, ] = useState(banner?.admins ?? false);
    const [remindMeLater, setRemindMeLater] = useState(banner?.remind_me_later ?? false);
    const [likeButton, setLikeButton ] = useState(banner?.like_button ?? false);
    const [notificationType,setNotificationType ] = useState(banner?.notification_type ?? "announcement");
    const [cta, setCta ] = useState(banner?.cta || false);
    const [ctaOption, setCtaOption] = useState(banner?.cta_text || "");
    const [ctaUrl, setCtaUrl] = useState(banner?.cta_url || "");
    const [startDate, setStartDate] = useState<Date | null>(banner?.start_date ? new Date(banner.start_date) : null);
    const [endDate, setEndDate] = useState<Date | null>(banner?.end_date ? new Date(banner.end_date) : null);
    const [schools, setSchools] = useState<School[]>([]);
    const [selectedSchools, setSelectedSchools] = useState<School[]>([]);
    const [selectedDataSources, setSelectedDataSources] = useState<LabelValueOption[]>(
        !banner ? [] : banner.data_sources?.map(datasource => ({value: datasource.datasource, label: datasource.datasource}))
    );
    const [selectedUserType, setSelectedUserType] = useState(banner?.users_type || "all");
    const [isUpdating, setIsUpdating] = useState(false);

    const onChangeText = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (ev.target.value.length > 90) {
            return;
        }
        setText(ev.target.value);
    };

    const onChangeDescription = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setDescription(ev.target.value);
    };

    const onChangeStartDate = (date: Date | null) => {
        setStartDate(date);
    };

    const onChangeEndDate = (date: Date | null) => {
        setEndDate(date);
    };

    const onSchoolChange = (newSelectedSchools: School[]) => {
        setSelectedSchools(newSelectedSchools);
    };

    const onDataSourceChange = (newSelectedDataSources: LabelValueOption[]) => {
        setSelectedDataSources(newSelectedDataSources);
    };

    const handleClearFields = () => {
        setText("");
        setDescription("");
        setStartDate(null);
        setEndDate(null);
        setSelectedSchools([]);
        setSchoolTypes([]);
        setSelectedDataSources([]);
        setSelectedUserType("all");
        setLikeButton(false);
        setRemindMeLater(false);
        setCta(false);
        setCtaOption("");
        setCtaUrl("");
    };

    const handleCancel = () => {
        onCancel();
    };

    const getCurrentBanner = (): Banner => {
        if (banner) {
            return {
                ...banner,
                banner_text: text,
                description: description,
                enabled: isEnabled,
                admins: isAdmins,
                start_date: startDate?.toISOString() ?? null,
                end_date: endDate?.toISOString() ?? null,
                color: color,
                background_color: backgroundColor,
                links_color: linksColor,
                notification_type: notificationType,
                remind_me_later: remindMeLater,
                like_button: likeButton,
                cta: cta,
                cta_text: ctaOption,
                cta_url: ctaUrl,
                users_type: selectedUserType,
            };
        } else {
            return {
                ruuid: "",
                school_id: "",
                banner_text: text,
                description: description,
                enabled: isEnabled,
                admins: isAdmins,
                start_date: startDate?.toISOString() ?? null,
                end_date: endDate?.toISOString() ?? null,
                color: color,
                background_color: backgroundColor,
                links_color: linksColor,
                schools: [],
                data_sources: [],
                users: [],
                school_types: [],
                notification_type: notificationType,
                cta: cta,
                cta_text: ctaOption,
                cta_url: ctaUrl,
                remind_me_later: remindMeLater,
                like_button: likeButton,
                users_type: selectedUserType,
            };
      }
    };

    const handleSave = () => {
        const schoolsIds = selectedSchools.map(school => school.school_id);
        const schoolTypesSelected = schoolTypes.map(schoolType => schoolType.value);
        const dataSources = selectedDataSources.map(dataSource => dataSource.value);
        const currentBanner = getCurrentBanner();
        if (banner) {
            onSave(currentBanner, schoolsIds, schoolTypesSelected, dataSources, banner.ruuid);
        } else {
            onSave(currentBanner, schoolsIds, schoolTypesSelected, dataSources);
        }
    };

    const getToday = () => {
        return new Date();
    };

    const isValid = () => {
        const valid = !(text.length === 0
            || color.length === 0
            || backgroundColor.length === 0
            || linksColor.length === 0);

        if (!valid) {
            return false;
        }

        if (!startDate) {
            return false;
        }

        if (startDate && endDate) {
            if (endDate <= startDate) {
                return false;
            }
        }

        if (cta && (ctaUrl.length === 0 || !ctaUrl.startsWith("https"))) {
            return false;
        }

        return true;

    };

    const renderPreview = () => {
        return (
            <BannerItem item={getCurrentBanner()} isAuthenticated={true} />
        );
    };

    useEffect(() => {
        reaction(() => schoolStore.schools, newSchools => {
            setSchools(newSchools);
            if (banner) {
                bannerStore.fetchBannerSchools(banner.ruuid).then(newSelectedSchoolsIds => {
                    if (newSelectedSchoolsIds) {
                        const newSelectedSchools = newSchools.filter(school => newSelectedSchoolsIds.find(id => school.school_id === id));
                        setSelectedSchools(newSelectedSchools);
                    }
                });
            }
        });
    }, []);

    useEffect(() => {
       schoolStore.fetchSchools();
    }, []);

    useEffect(() => {
        reaction(() => bannerStore.isUpdatingBanner, newValue => {
            setIsUpdating(newValue);
        });
    }, []);

    return (
        <Form>
            <h2>Notifications</h2>
            <p>Create the notifications to send information to the users.</p>
            <Row className="mb-3">
                <Col xs={3}>
                    <Form.Label>Notification Type</Form.Label>
                    <Form.Select value={notificationType} onChange={event => setNotificationType(event.target.value)}>
                        <option value="announcement">Announcement</option>
                        <option value="new_feature">New Feature</option>
                        <option value="event">Event</option>
                        <option value="warning">Warning</option>
                        <option value="error">Error</option>
                    </Form.Select>
                </Col>
                <Col xs={9}>
                    <Form.Label>Title</Form.Label>
                    <Form.Control value={text} onChange={onChangeText}/>
                    <Form.Text muted>
                        Your title must be less than 90 characters long.
                    </Form.Text>
                </Col>
            </Row>
            <Form.Group className="mb-3">
                <Form.Label>Text</Form.Label>
                <Form.Control as="textarea" rows={3} value={description} onChange={onChangeDescription}/>
            </Form.Group>
            <Row className="mb-3">
                <Col>
                    <Form.Check
                        label="Like button"
                        type="switch"
                        checked={likeButton}
                        onChange={() => setLikeButton(!likeButton)}
                        id="like-button"
                    />
                </Col>
                <Col>
                    <Form.Check
                        label="Remind me later"
                        checked={remindMeLater}
                        onChange={() => setRemindMeLater(!remindMeLater)}
                        name="remind-me-later"
                        type="switch"
                        id="remind-me-later"
                    />
                </Col>
                <Col>
                    <Form.Check
                        label="CTA"
                        value="cta"
                        name="cta"
                        checked={cta}
                        onChange={() => {
                            setCtaOption("");
                            setCtaUrl("");
                            setCta(!cta);
                        }}
                        type="checkbox"
                        id="cta"
                    />
                </Col>
                <Col>
                    <Form.Select aria-label="cta option" disabled={!cta} value={ctaOption}
                                 onChange={ev => setCtaOption(ev.target.value)}>
                        <option>Select one option</option>
                        <option value="learn_more">Learn More</option>
                        <option value="letsgo">Let&apos;s Go</option>
                        <option value="register">Register</option>
                    </Form.Select>
                </Col>
                <Form.Group className="mb-3" as={Col}>
                    <Form.Control placeholder="CTA URL" value={ctaUrl} disabled={!cta}
                                  onChange={event => setCtaUrl(event.target.value)}/>
                </Form.Group>
            </Row>
            <Row>
                <Form.Group className="mb-3" as={Col}>
                    <Form.Label>Start Date</Form.Label>
                    <DatePicker
                        placeholderText="Start Date"
                        className="form-control"
                        selected={startDate}
                        minDate={getToday()}
                        onChange={onChangeStartDate}
                    />
                </Form.Group>
                <Form.Group className="mb-3" as={Col}>
                    <Form.Label>Start Hour</Form.Label>
                    <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={60}
                        timeCaption="Time"
                        dateFormat="h:mm aa"
                        className="form-control"
                    />
                </Form.Group>
                <Form.Group className="mb-3" as={Col}>
                    <Form.Label>End Date</Form.Label>
                    <DatePicker
                        placeholderText="Start Date"
                        className="form-control"
                        selected={endDate}
                        minDate={getToday()}
                        onChange={onChangeEndDate}
                    />
                </Form.Group>
                <Form.Group className="mb-3" as={Col}>
                    <Form.Label>End Hour</Form.Label>
                    <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={60}
                        timeCaption="Time"
                        dateFormat="h:mm aa"
                        className="form-control"
                    />
                </Form.Group>
            </Row>
            <Row>
                <Form.Group className="mb-3" as={Col}>
                    <Form.Label>School Type</Form.Label>
                    <SelectWrapper
                        name="school-type-select"
                        options={SCHOOL_TYPE_OPTIONS}
                        isMulti={true}
                        onChange={ (option: LabelValueOption[]) => setSchoolTypes(option)}
                        value={ schoolTypes }
                        isClearable={true}
                    />
                </Form.Group>
                <Form.Group className="mb-3" as={Col}>
                    <Form.Label>Schools</Form.Label>
                    <SelectWrapper
                        hideSelectedOptions={true}
                        onChange={onSchoolChange}
                        isClearable={true}
                        isMulti={true}
                        options={schools}
                        value={selectedSchools}
                        getOptionLabel={school => school.school_id}
                        getOptionValue={school => school.school_id}
                    />
                </Form.Group>
                <Form.Group className="mb-3" as={Col}>
                    <Form.Label>Data Source</Form.Label>
                    <SelectWrapper
                        hideSelectedOptions={true}
                        onChange={onDataSourceChange}
                        isClearable={true}
                        isMulti={true}
                        options={SCHOOL_DATA_SOURCE_OPTIONS}
                        value={selectedDataSources}
                    />
                </Form.Group>
                <Form.Group className="mb-3" as={Col}>
                    <Form.Label>User Types</Form.Label>
                    <Form.Select value={selectedUserType} onChange={event => setSelectedUserType(event.target.value)}>
                        <option value="all">All Users</option>
                        <option value="key_admins">Key Admin</option>
                        <option value="admins">Admins</option>
                    </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3" as={Col}>
                    <Form.Label>Notifications Status</Form.Label>
                    <div className="d-flex align-items-center">
                        <span className="me-2">Inactive</span>
                        <Form.Check
                            type="switch"
                            inline
                            checked={isEnabled}
                            onChange={() => setIsEnabled(!isEnabled)}
                            className="me-1"
                            id="notification_status"
                        />
                        <span className="ms-1">Active</span>
                    </div>
                </Form.Group>
            </Row>
            {banner && (
                <Row>
                    <Col></Col>
                    <Col className="text-end">
                        Created By: {banner.created_by_name}
                    </Col>
                </Row>
            )}
            <hr className="hr"/>
            <h5 className="text-center">Preview:</h5>
            <div className="text-center">
                {renderPreview()}
            </div>
            <Row>
                <Col className="text-end">
                    <Button variant="light" onClick={handleClearFields}>Clear fields</Button>
                    <Button
                        variant="secondary"
                        size="lg"
                        className="ms-2"
                        disabled={false}
                        onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="lg"
                        className="ms-2"
                        disabled={!isValid() || isUpdating}
                        onClick={handleSave}>
                        Save
                    </Button>
                </Col>
            </Row>
        </Form>
    );
}

export default BannerForm;
